import * as React from 'react';

import * as UI from 'core/ui';
import { SalesOrderDTO, Res, DeliveryStatus } from 'core/api';
import { Result } from 'core/infrastructure';
import { Button } from 'core/ui';
import { canTransitionToAccept, canTransitionToCancel, canTransitionToDelivered, canTransitionToDelivering, canTransitionToPrepare, canTransitionToPrepared } from './transitions';
import { toModel } from './SalesOrderModel';
import { Command } from 'pos/common';
import { Modal } from 'core/ui-next/modal';
import { Box, DialogBody, DialogFooter, DialogHeader, DialogTitle } from '@chakra-ui/react';

type Props = {
	salesOrder: SalesOrderDTO
} & Modal.Actions<void>;


class SetTakeOutOrderStatusDialog extends UI.StatelessComponent<Props> {
	onComponentDidMount(): void { }
	onComponentWillUnmount(): void { }

	cancel() {
		this.props.cancel();
	}

	accept = async () => {
		await Command.execute('acceptTakeOutOrder', this.props.salesOrder.Id);
		this.props.close();
	};

	reject = async () => {
		await Command.execute('rejectTakeOutOrder', this.props.salesOrder.Id);
		this.props.close();
	};

	prepare = async () => {
		await Command.execute('prepareSalesOrder', this.props.salesOrder);
		this.props.close();
	};

	markAsPrepared = async () => {
		await Command.execute('markTakeOutOrderAsPrepared', this.props.salesOrder.Id);
		this.props.close();
	};

	deliver = async () => {
		await Command.execute('deliverTakeOutOrder', this.props.salesOrder.Id, this.props.salesOrder.DeliveryPerson);
		this.props.close();
	};

	changeDeliveryPerson = async () => {
		await Command.execute('changeTakeOutOrderDeliveryPerson', this.props.salesOrder.Id, this.props.salesOrder.DeliveryPerson, 'WARN_IF_NO_DELIVERY_PERSONS');
		this.props.close();
	};

	markAsDelivered = async () => {
		await Command.execute('markTakeOutOrderAsDelivered', this.props.salesOrder.Id);
		this.props.close();
	};

	cancelOrder = async () => {
		const order = toModel(this.props.salesOrder);
		if (order.DeliveryStatus !== DeliveryStatus.New) {
			await Command.execute('cancelTakeOutOrder', { order, allowCancelWithPreviousPayments: true });
		} else {
			await Command.execute('rejectTakeOutOrder', this.props.salesOrder.Id);
		}

		this.props.close();
	};

	getDeliveryingOrChangeDeliveryPersonButton = () => {
		if (canTransitionToDelivering(this.props.salesOrder))
			return <Button className='btn-block' onClick={this.deliver}>{Res.Web.SET_TAKE_OUT_ORDER_DELIVERING_ORDER_BTN()}</Button>;

		if (this.props.salesOrder.DeliveryStatus === DeliveryStatus.Delivering)
			return <Button className='btn-block' onClick={this.changeDeliveryPerson}>{Res.Web.SET_TAKE_OUT_ORDER_CHANGE_DELIVERY_PERSON_BTN()}</Button>;

		return null;
	};

	render() {
		const cancelText = (order: SalesOrderDTO) => order.DeliveryStatus === DeliveryStatus.New ? Res.Web.SHOW_TAKE_OUT_ORDERS_REJECT_ORDER() : Res.Web.SHOW_TAKE_OUT_ORDERS_CANCEL_ORDER();

		return <Box className='legacy-styles'>
			<DialogHeader>
				<DialogTitle>{Res.Web.SET_TAKE_OUT_ORDER_STATUS()}</DialogTitle>
			</DialogHeader>
			<DialogBody >
				<div className='text-center'>
					{canTransitionToAccept(this.props.salesOrder) ? <Button className='btn-block' onClick={this.accept}>{Res.Web.SET_TAKE_OUT_ORDER_ACCEPT_ORDER_BTN()}</Button> : null}
					{canTransitionToCancel(this.props.salesOrder) ? <Button className='btn-block' onClick={this.cancelOrder}>{cancelText(this.props.salesOrder)}</Button> : null}
					{canTransitionToPrepare(this.props.salesOrder) ? <Button className='btn-block' onClick={this.prepare}>{Res.Web.SET_TAKE_OUT_ORDER_PREPARE_ORDER_BTN()}</Button> : null}
					{canTransitionToPrepared(this.props.salesOrder) ? <Button className='btn-block' onClick={this.markAsPrepared}>{Res.Web.SET_TAKE_OUT_ORDER_PREPARED_ORDER_BTN()}</Button> : null}
					{this.getDeliveryingOrChangeDeliveryPersonButton()}
					{canTransitionToDelivered(this.props.salesOrder) ? <Button className='btn-block' onClick={this.markAsDelivered}>{Res.Web.SET_TAKE_OUT_ORDER_DELIVERED_ORDER_BTN()}</Button> : null}
				</div>
			</DialogBody>
			<DialogFooter>
				<Button onClick={() => this.cancel()}>{Res.Common.CANCEL_BTN()}</Button>
			</DialogFooter>
		</Box>;
	}
}

export default async function setTakeOutOrderStatus(salesOrder: SalesOrderDTO): Promise<Result> {
	const result = await Modal.show(SetTakeOutOrderStatusDialog, { salesOrder, placement: 'top', size: 'sm' });

	if (!result.ok)
		return Result.error();

	return Result.ok();
}